import { PermissionValues as Permissions } from '@gripp/shared-logic';
import { ActivityEdit } from '../activity/components/activity-edit';
import { ActivityReassign } from '../activity/components/activity-reassign';
import { ActivityRevise } from '../activity/components/activity-revise';
import { Issue } from '../issues/components/issue';
import { getCurrentDimensions } from '../shared/hooks/useWindowDimensions';
import { assetSubRoutes } from './assetSubRoutes';
import { assetPaths } from './assetUtils';
import { Asset } from './components/asset';
import { AssetAlertFormPage } from './components/asset-alert-form-page';
import { AssetRoutineAlerts } from './components/asset-routine-alerts';
import { AssetRoutineSubmit } from './components/asset-routine-submit';
import { AssetRoutineSubmitRoute } from './components/asset-routine-submit-route';
import { AssetRoutines } from './components/asset-routines';
import { AssetRoutinesAll } from './components/asset-routines-all';
import { AssetRoutinesRearrange } from './components/asset-routines-rearrange';
import { AssetActivity } from './components/assetActivity';
import { AssetDetail } from './components/assetDetail';
import { AssetDetailFilter } from './components/assetDetailFilter';
import { AssetDetailPanel } from './components/assetDetailPanel';
import { AssetFileEdit } from './components/assetFileEdit';
import { AssetFilePreview } from './components/assetFilePreview';
import { AssetFiles } from './components/assetFiles';
import { AssetNew } from './components/assetNew';
import { AssetNewIssue } from './components/assetNewIssue';
import { Assets } from './components/assets';
import { AssetsFilterPage } from './components/assetsFilterPage';
import { EditAsset } from './components/edit';

export const assetsRoutes = [
  {
    path: assetPaths.root,
    element: <Assets />,
    handle: {
      header: {
        titleKey: 'assets.menu.title',
        icon: 'qrcode',
      },
      requiredPermission: Permissions.asset.read,
    },
    children: [
      {
        path: 'filters',
        element: <AssetsFilterPage />,
        handle: {
          panel: { key: 'asset-filters', title: 'assets.menu.title' },
          header: {
            titleKey: 'assets.menu.title',
            icon: 'qrcode',
          },
          requiredPermission: Permissions.asset.read,
        },
      },
      {
        path: 'new',
        element: <AssetNew />,
        handle: {
          panel: { key: 'asset-new', title: 'assets.menu.title' },
          header: {
            titleKey: 'assets.menu.title',
            icon: 'qrcode',
          },
          requiredPermission: Permissions.asset.create,
        },
      },
      {
        path: ':assetId',
        element: <AssetDetailPanel />,
        handle: {
          panel: { key: 'asset-detail', title: 'assets.menu.title' },
          header: {
            titleKey: 'assets.menu.title',
            icon: 'qrcode',
          },
          requiredPermission: Permissions.asset.read,
        },
        children: [
          {
            path: 'routines/:routineId',
            element: <AssetRoutineSubmitRoute />,
            handle: {
              panel: {
                key: 'asset-routine-submit',
                title: 'assets.menu.title',
              },
              header: {
                titleKey: 'assets.menu.title',
                icon: 'qrcode',
              },
              requiredPermission: Permissions.asset.read,
            },
          },
        ],
      },
    ],
  },
  // Nested sub-routes with handle.panel metadata for PanelShell transitionKey.
  // These match the same URL patterns as the legacy flat entries below; placing
  // them first ensures React Router picks up the handle.panel field.
  // Legacy flat entries are kept per CLAUDE.md's additive-only rule.
  ...assetSubRoutes.map((r) => ({
    ...r,
    path: `${assetPaths.root}/:assetId/${r.path}`,
  })),

  {
    path: `${assetPaths.root}/:assetId/activity/new`,
    element: <AssetNewIssue />,
    handle: {
      header: {
        titleKey: 'assets.menu.title',
        icon: 'qrcode',
      },
      requiredPermission: Permissions.issue.create,
    },
  },
  {
    path: `${assetPaths.root}/:assetId/routines`,
    element: <AssetRoutines />,
    handle: {
      header: {
        titleKey: 'assets.menu.title',
        icon: 'qrcode',
      },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: `${assetPaths.root}/:assetId/routines/all`,
    element: <AssetRoutinesAll />,
    handle: {
      header: {
        titleKey: 'assets.menu.title',
        icon: 'qrcode',
      },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: `${assetPaths.root}/:assetId/routines/edit`,
    element: <AssetRoutinesRearrange />,
    handle: {
      header: {
        titleKey: 'assets.menu.title',
        icon: 'qrcode',
      },
      requiredPermission: Permissions.asset.update,
    },
  },
  {
    path: `${assetPaths.root}/:assetId/routines/:routineId`,
    element: <AssetRoutineSubmit />,
    handle: {
      header: {
        titleKey: 'assets.menu.title',
        icon: 'qrcode',
      },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: `${assetPaths.root}/:assetId/routines/all/:routineId`,
    element: <AssetRoutineSubmit />,
    handle: {
      header: {
        titleKey: 'assets.menu.title',
        icon: 'qrcode',
      },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: `${assetPaths.root}/:assetId/routines/:routineId/alerts`,
    element: <AssetRoutineAlerts />,
    handle: {
      header: {
        titleKey: 'assets.menu.title',
        icon: 'qrcode',
      },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: `${assetPaths.root}/:assetId/routines/:routineId/alerts/new`,
    element: <AssetAlertFormPage />,
    handle: {
      header: {
        titleKey: 'assets.menu.title',
        icon: 'qrcode',
      },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: `${assetPaths.root}/:assetId/routines/:routineId/alerts/:alertId/edit`,
    element: <AssetAlertFormPage />,
    handle: {
      header: {
        titleKey: 'assets.menu.title',
        icon: 'qrcode',
      },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: `${assetPaths.root}/:assetId/activity/:issueId`,
    element: <AssetActivity />,
    handle: {
      header: {
        titleKey: 'assets.menu.title',
        icon: 'qrcode',
      },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: `${assetPaths.root}/:assetId/files/all`,
    element: <AssetFiles />,
    handle: {
      header: {
        titleKey: 'assets.menu.title',
        icon: 'qrcode',
      },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: `${assetPaths.root}/:assetId/files/:mediaId`,
    element: <AssetFilePreview />,
    handle: {
      header: {
        titleKey: 'assets.menu.title',
        icon: 'qrcode',
      },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: `${assetPaths.root}/:assetId/files/:mediaId/edit`,
    element: <AssetFileEdit />,
    handle: {
      header: {
        titleKey: 'assets.menu.title',
        icon: 'qrcode',
      },
      requiredPermission: Permissions.asset.update,
    },
  },
  {
    path: `${assetPaths.root}/:assetId/activity/all`,
    element: <AssetDetail />,
    handle: {
      header: {
        titleKey: 'topNav.assetDetail',
        icon: 'qrcode',
      },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: `${assetPaths.root}/:assetId/activity/filters`,
    element: <AssetDetailFilter />,
    handle: {
      header: {
        titleKey: 'assets.menu.title',
        icon: 'qrcode',
      },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: 'assets/:assetId/edit',
    element: <EditAsset />,
    handle: {
      header: {
        titleKey: 'topNav.editAsset',
        icon: 'qrcode',
      },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: `${assetPaths.root}/:assetId/activity/:issueId/edit`,
    element: <ActivityEdit />,
    handle: {
      header: { titleKey: 'activity.menu.title', icon: 'bell-outline' },
      requiredPermission: Permissions.issue.update,
    },
  },
  {
    path: `${assetPaths.root}/:assetId/activity/:issueId/edit/reassign`,
    element: <ActivityReassign />,
    handle: {
      header: {
        titleKey: 'activity.menu.title',
        icon: 'bell-outline',
      },
      requiredPermission: Permissions.issue.update,
    },
  },
  {
    path: `${assetPaths.root}/:assetId/activity/:issueId/edit/revise`,
    element: <ActivityRevise />,
    handle: {
      header: {
        titleKey: 'activity.menu.title',
        icon: 'bell-outline',
      },
      requiredPermission: Permissions.issue.update,
    },
  },
  {
    path: `${assetPaths.root}/:assetId`,
    element: <Asset />,
    handle: {
      header: {
        titleKey: 'assets.menu.title',
        icon: 'qrcode',
      },
      requiredPermission: Permissions.asset.read,
    },
    children: [
      {
        path: 'activities/:issueId',
        element: <Issue />,
        loader: () => {
          const dimensions = getCurrentDimensions();
          const maxHeight = (dimensions?.height || 0) - 580;

          return {
            dimensions: dimensions,
            maxHeight: maxHeight,
          };
        },
      },
    ],
  },
];
